/**
 * Playground Blueprint Builder
 *
 * Constructs Playground blueprints for Miles sites. Handles:
 * - Plugin installation (always)
 * - ZIP import: theme install, upload files, database import, URL rewriting
 * - Site options and login
 */

import { debugLog } from '@/lib/debug-log';
import { API_URL, SERVER_URL } from './api';
import { SNAPSHOT_PLUGIN_UPLOAD_EXTRACTOR_PHP } from './playground-snapshot-extractor';

declare const __PLAYGROUND_PORT__: string;
const playgroundPort = __PLAYGROUND_PORT__;
const playgroundRemoteBaseUrl =
  import.meta.env.VITE_PLAYGROUND_URL ||
  (import.meta.env.DEV
    ? `http://localhost:${playgroundPort}/remote.html`
    : 'https://playground.bymiles.ai/remote.html');
const playgroundRemoteUrl = new URL(playgroundRemoteBaseUrl);
playgroundRemoteUrl.searchParams.set('miles-bridge', '20260724-boot-stages-v5');
const PLAYGROUND_REMOTE_URL = playgroundRemoteUrl.toString();

export const PLAYGROUND_ORIGIN = playgroundRemoteUrl.origin;

// Share links open on their own host when one is configured, so shared sites
// do not run on the origin that stores the user's Playground sites.
function sharedSiteOrigin(): string {
  const configured = import.meta.env.VITE_SHARED_SITE_URL;
  if (!configured) return PLAYGROUND_ORIGIN;
  try {
    return new URL(configured).origin;
  } catch {
    return PLAYGROUND_ORIGIN;
  }
}

export const SHARED_SITE_ORIGIN = sharedSiteOrigin();

export const PLAYGROUND_PREFERRED_VERSIONS = {
  wp: '7.1',
  php: '8.4',
} as const;

export function getPlaygroundRuntimeBlueprint(wpVersion?: string): {
  preferredVersions: { wp: string; php: string };
  phpExtensionBundles: ['kitchen-sink'];
  features: { networking: true };
  steps: Array<{ step: 'runPHP'; code: string }>;
} {
  return {
    preferredVersions: {
      ...PLAYGROUND_PREFERRED_VERSIONS,
      ...(wpVersion ? { wp: wpVersion } : {}),
    },
    phpExtensionBundles: ['kitchen-sink'],
    features: { networking: true },
    steps: [getPlaygroundCompatMuPluginStep()],
  };
}

export function getPlaygroundCompatMuPluginStep(): {
  step: 'runPHP';
  code: string;
} {
  return {
    step: 'runPHP',
    code: `<?php
      @mkdir('/wordpress/wp-content/mu-plugins', 0777, true);
      $compat_plugin = <<<'MILES_COMPAT_PHP'
<?php
// Prevent plugins from spawning background HTTP requests that exhaust
// Playground's limited PHP instance pool.
//
// Two layers of defense:
// 1. Targeted filters — disable known heavy background processes
// 2. pre_http_request — catch-all that blocks ANY plugin's loopback
//    requests to wp-cron.php and admin-ajax.php from PHP

// Layer 1: Disable known background process triggers
add_filter( "action_scheduler_allow_async_request_runner", "__return_false", 0 );
add_filter( "woocommerce_background_image_regeneration", "__return_false", 0 );

// Layer 2: Block all server-to-server loopback requests to cron and AJAX endpoints.
// This catches any plugin spawning background HTTP requests we don't know about.
// Only affects PHP-side wp_remote_post/get calls, not browser AJAX requests.
add_filter( "pre_http_request", function( $pre, $args, $url ) {
    if ( preg_match( "#/wp-cron\\.php|/wp-admin/admin-ajax\\.php#", $url ) ) {
        return new WP_Error( "playground_loopback_blocked",
            "Loopback request blocked to protect Playground PHP instance limit"
        );
    }
    return $pre;
}, 0, 3 );

// The WordPress news widget fetches external RSS feeds as soon as wp-admin
// opens. In Playground those PHP requests are browser-proxied, so feeds that
// do not allow cross-origin access print alarming CORS errors even though the
// widget is irrelevant to the Miles workspace.
add_action( "wp_dashboard_setup", function() {
    remove_meta_box( "dashboard_primary", "dashboard", "side" );
} );

// Miles does not use jQuery Migrate. Exclude the legacy compatibility layer
// throughout Playground so intermediate and final wp-admin screens stay quiet
// without changing WordPress behavior on a user's real installation.
add_action( "wp_default_scripts", function( $scripts ) {
    if ( ! isset( $scripts->registered["jquery"] ) ) {
        return;
    }

    $scripts->registered["jquery"]->deps = array_values( array_diff(
        $scripts->registered["jquery"]->deps,
        ["jquery-migrate"]
    ) );
} );
MILES_COMPAT_PHP;
      $written = file_put_contents(
          '/wordpress/wp-content/mu-plugins/miles-playground-compat.php',
          $compat_plugin
      );
      if ($written === false) {
          error_log('Miles: Failed to write mu-plugin miles-playground-compat.php');
      }
    `,
  };
}

export { PLAYGROUND_REMOTE_URL };

function isLocalUrl(url: string): boolean {
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(url);
}

export function getImportedMilesOptionsCleanupStep(): { step: 'runPHP'; code: string } {
  return {
    step: 'runPHP',
    code: `<?php
      require '/wordpress/wp-load.php';
      global $wpdb;
      $result = $wpdb->query("DELETE FROM {$wpdb->options} WHERE option_name LIKE 'miles_%'");
      if ($result === false) {
        throw new RuntimeException('Failed to clear imported Miles connection options: ' . $wpdb->last_error);
      }
    `,
  };
}

export interface ImportData {
  siteName?: string;
  themeSlug: string;
  // Snapshot or export ZIP (handling depends on whether databaseUrl is also present)
  snapshotUrl?: string;
  // Established by server-controlled generation, never read from the imported ZIP.
  managedThemeSlugs?: string[];
  // Fallback snapshot URL if primary is corrupt (immutable conversion snapshot)
  snapshotConversionUrl?: string;
  themeOverrideUrl?: string;
  // Existing import format (per-file restore)
  themeUrl?: string;
  databaseUrl?: string | null;
  uploads?: Array<{ path: string; url: string }>;
}

/**
 * Fetch import data for a site (if it was created via ZIP import).
 */
export async function fetchImportData(
  siteId: string,
): Promise<ImportData | null> {
  const res = await fetch(
    `${API_URL}/api/v2/cloud/sites/${siteId}/import-data`,
    { credentials: 'include' },
  );
  if (!res.ok) {
    throw new Error(`Failed to fetch import data: HTTP ${res.status}`);
  }
  const data = await res.json();
  if (!data.hasImport) return null;
  return data as ImportData;
}

interface BuildBlueprintParams {
  siteId: string;
  bootAttemptId: string;
  importData: ImportData | null;
  /** Server-reported plugin version, used to cache-bust the production zip URL */
  pluginVersion?: string;
  onProgress?: (message: string, progress: number) => void;
  /** When true, appends &agent=true to the landing page URL to hide the chat panel */
  agentMode?: boolean;
  /** Whether this boot is restoring an existing site from OPFS. */
  siteExistsInOPFS: boolean;
  /** The wp-admin handoff already chose the design intent. */
  seededHandoff?: boolean;
}

export function getPlaygroundBlueprintLandingPage(input: {
  agentMode?: boolean;
  siteExistsInOPFS: boolean;
  seededHandoff?: boolean;
}): string {
  if (input.siteExistsInOPFS) {
    return '/wp-admin/';
  }

  const params = new URLSearchParams({ page: 'miles' });
  if (input.agentMode) params.set('agent', 'true');
  if (input.seededHandoff) params.set('step', 'build');
  return `/wp-admin/admin.php?${params.toString()}`;
}

/**
 * Build a Playground blueprint for a Miles site.
 *
 * Fetches the plugin ZIP (and import assets if applicable), then returns
 * a complete blueprint object ready to pass to startPlaygroundWeb().
 */
export async function buildBlueprint({
  siteId,
  bootAttemptId,
  importData,
  pluginVersion,
  onProgress,
  agentMode,
  siteExistsInOPFS,
  seededHandoff,
}: BuildBlueprintParams): Promise<object> {
  const isDev = import.meta.env.DEV;
  const shouldUseLocalPluginZip =
    isDev || isLocalUrl(PLAYGROUND_REMOTE_URL) || isLocalUrl(SERVER_URL);

  // Fetch plugin ZIP
  // Dev: from local playground-host (live from source)
  // Local preview builds: also use the live local ZIP. Vite preview has
  // import.meta.env.DEV === false but still runs against localhost
  // Playground/server, so using the production ZIP hides local plugin assets.
  // Prod: from API server (serves the version pointed to by MILES_PLUGIN_VERSION).
  // Version-key the URL so the browser HTTP cache (max-age=300) doesn't serve
  // stale bytes after a version change — without this, rollbacks/upgrades can
  // install the wrong zip while localStorage records the new version label.
  onProgress?.('Preparing your workspace...', 16);
  const timestamp = Date.now();
  const productionZipUrl = pluginVersion
    ? `${API_URL}/api/v2/cloud/plugin.zip?v=${encodeURIComponent(pluginVersion)}`
    : `${API_URL}/api/v2/cloud/plugin.zip`;
  const pluginZipUrl = shouldUseLocalPluginZip
    ? `http://localhost:${playgroundPort}/api/dev/miles-plugin.zip?t=${timestamp}`
    : productionZipUrl;

  const pluginZipPromise = fetch(pluginZipUrl);
  // Awaited just below; the guard only suppresses unhandled-rejection noise
  // if an eagerly-started import fetch settles first.
  pluginZipPromise.catch(() => {});

  // Start the import branch's multi-MB downloads (theme/snapshot/database)
  // now so they overlap the plugin download instead of running after it.
  const prefetched = importData ? startImportFetches(importData) : {};

  const pluginRes = await pluginZipPromise;
  if (!pluginRes.ok) {
    throw new Error(`Failed to fetch plugin: ${pluginRes.status}`);
  }
  const pluginZipBytes = new Uint8Array(await pluginRes.arrayBuffer());

  const steps: object[] = [
    { step: 'login', username: 'admin', password: 'password' },
  ];

  // Install a mu-plugin that prevents heavy plugins (WooCommerce, Action
  // Scheduler, etc.) from spawning background HTTP requests. Playground has a
  // limited PHP instance pool — loopback cron runs, async queue runners, and
  // image regeneration requests exhaust that limit and cause
  // MaxPhpInstancesError. This mu-plugin loads before regular plugins and
  // disables the relevant hooks/constants.
  steps.push(getPlaygroundCompatMuPluginStep());

  // Add import steps BEFORE plugin install — imported ZIPs may contain an old
  // copy of Miles, so we install the fresh plugin afterwards to overwrite it.
  if (importData) {
    await addImportSteps(steps, siteId, importData, prefetched, onProgress);
    steps.push({
      step: 'setSiteOptions',
      options: { miles_managed_themes: importData.managedThemeSlugs ?? [] },
    });
  }

  // Install plugin after import so the fresh version always wins
  steps.push({
    step: 'installPlugin',
    pluginData: {
      resource: 'literal',
      name: 'miles-plugin.zip',
      contents: pluginZipBytes,
    },
    ifAlreadyInstalled: 'overwrite',
    options: { activate: true },
  });

  // Configure Miles options (and override blogname for imported sites).
  // The single-use token is intentionally NOT stored here: the dashboard
  // exchanges it in its own network context and injects the resulting secret
  // via a direct PHP call (see establishPlaygroundCredentials), so no boot
  // secret ever crosses the wp-admin JavaScript boundary or triggers outbound
  // HTTP from inside Playground's PHP-WASM instance pool.
  const siteOptions: Record<string, string> = {
    miles_playground_site_id: siteId,
    miles_is_playground: '1',
    miles_server_url: SERVER_URL,
    miles_playground_boot_attempt_id: bootAttemptId,
  };
  if (importData?.siteName) {
    siteOptions.blogname = importData.siteName;
  }
  steps.push({
    step: 'setSiteOptions',
    options: siteOptions,
  });

  // Ensure logged in as admin after any DB import
  steps.push({
    step: 'login',
    username: 'admin',
    password: 'password',
  });

  // Remove Hello Dolly — WordPress bundles it by default but it signals
  // "stock install" and users immediately uninstall it.
  steps.push({
    step: 'runPHP',
    code: `<?php
      @unlink('/wordpress/wp-content/plugins/hello.php');
    `,
  });

  // Disable welcome guides and close editor sidebars
  steps.push({
    step: 'runPHP',
    code: `<?php
      require '/wordpress/wp-load.php';
      $user_id = 1;
      $prefs = get_user_meta( $user_id, 'wp_persisted_preferences', true );
      if ( ! is_array( $prefs ) ) { $prefs = []; }

      // Disable all welcome guides
      $prefs['core/edit-post'] = array_merge( $prefs['core/edit-post'] ?? [], [
        'welcomeGuide' => false,
        'welcomeGuideTemplate' => false,
      ]);
      $prefs['core/edit-site'] = array_merge( $prefs['core/edit-site'] ?? [], [
        'welcomeGuide' => false,
        'welcomeGuideStyles' => false,
        'welcomeGuideTemplate' => false,
        'welcomeGuidePage' => false,
      ]);
      $prefs['core/edit-widgets'] = array_merge( $prefs['core/edit-widgets'] ?? [], [
        'welcomeGuide' => false,
      ]);
      $prefs['core/customize-widgets'] = array_merge( $prefs['core/customize-widgets'] ?? [], [
        'welcomeGuide' => false,
      ]);

      // Close settings sidebar and pattern modal
      if ( ! isset( $prefs['core'] ) ) { $prefs['core'] = []; }
      $prefs['core']['isComplementaryAreaVisible'] = false;
      $prefs['core']['enableChoosePatternModal'] = false;

      update_user_meta( $user_id, 'wp_persisted_preferences', $prefs );
    `,
  });

  // In dev mode, create a mu-plugin that enables Vite HMR inside Playground.
  // This makes the Miles plugin load React code from the Vite dev server
  // instead of from dist/ files, giving full HMR without needing a blueprint on refresh.
  if (isDev) {
    const vitePort = import.meta.env.VITE_PORT || '5173';
    steps.push({
      step: 'runPHP',
      code: `<?php
        @mkdir('/wordpress/wp-content/mu-plugins', 0777, true);
        file_put_contents('/wordpress/wp-content/mu-plugins/miles-dev-mode.php',
          "<?php\\ndefine('MILES_DEV_MODE', true);\\ndefine('MILES_VITE_PORT', '${vitePort}');\\n"
        );
      `,
    });
  }

  // Flush rewrite rules as the final step.
  // Plugin installation, theme activation, and DB imports can leave stale
  // rules that prevent REST API endpoints from resolving.
  steps.push({
    step: 'runPHP',
    code: `<?php
      require '/wordpress/wp-load.php';
      flush_rewrite_rules(true);
    `,
  });

  return {
    landingPage: getPlaygroundBlueprintLandingPage({
      agentMode,
      siteExistsInOPFS,
      seededHandoff,
    }),
    ...getPlaygroundRuntimeBlueprint(),
    steps,
  };
}

/** Minimum size for a valid WordPress snapshot ZIP (sanity check to catch truncated uploads or empty responses) */
const MIN_SNAPSHOT_BYTES = 50_000;

function isValidZip(bytes: Uint8Array): boolean {
  // ZIP magic bytes (PK header: 0x50 0x4B) + minimum size sanity check
  return (
    bytes.length >= MIN_SNAPSHOT_BYTES && bytes[0] === 0x50 && bytes[1] === 0x4b
  );
}

/**
 * Fetch and validate a single snapshot URL. Returns valid ZIP bytes or null.
 */
async function fetchValidatedSnapshot(
  url: string,
  label: string,
  credentials: RequestCredentials,
): Promise<Uint8Array | null> {
  try {
    const res = await fetch(url, { credentials });
    if (!res.ok) {
      console.warn(`[Blueprint] ${label} snapshot HTTP ${res.status}`);
      return null;
    }
    const bytes = new Uint8Array(await res.arrayBuffer());
    if (isValidZip(bytes)) return bytes;
    console.warn(
      `[Blueprint] ${label} snapshot invalid (${bytes.length} bytes, header: 0x${bytes[0]?.toString(16)}${bytes[1]?.toString(16)})`,
    );
    return null;
  } catch (err) {
    console.warn(`[Blueprint] ${label} snapshot network error:`, err);
    return null;
  }
}

/**
 * Fetch a snapshot ZIP with automatic fallback to conversion snapshot.
 * Validates ZIP magic bytes + minimum size to detect corrupt files
 * (zero-byte, truncated uploads, HTML error pages from S3).
 */
async function fetchSnapshotWithFallback(
  primaryUrl: string,
  fallbackUrl: string | undefined,
  credentials: RequestCredentials,
): Promise<Uint8Array> {
  const primary = await fetchValidatedSnapshot(
    primaryUrl,
    'Primary',
    credentials,
  );
  if (primary) return primary;

  if (fallbackUrl) {
    console.warn('[Blueprint] Retrying with conversion snapshot fallback');
    const fallback = await fetchValidatedSnapshot(
      fallbackUrl,
      'Fallback',
      credentials,
    );
    if (fallback) return fallback;
    throw new Error(
      'Both primary and fallback snapshots are corrupt or unavailable',
    );
  }

  throw new Error('Failed to fetch snapshot and no fallback available');
}

/**
 * Multi-MB downloads needed by addImportSteps, started eagerly so they
 * overlap the plugin ZIP download instead of running serially after it.
 * Each promise is awaited at the same point in addImportSteps where the
 * corresponding fetch used to run, so step order, progress messages, and
 * error precedence are unchanged.
 */
interface PrefetchedImportResources {
  themeOverride?: Promise<Response>;
  snapshot?: Promise<Uint8Array>;
  database?: Promise<Response>;
  theme?: Promise<Response>;
}

/**
 * Start the downloads for exactly the addImportSteps branch that will run.
 * The branch conditions MUST mirror addImportSteps — a mismatch only
 * degrades to the previous serial fetch behavior (each await site falls
 * back to its own fetch), never to a wrong request.
 */
function startImportFetches(importData: ImportData): PrefetchedImportResources {
  // Awaiting happens later, in source order; a sibling rejecting first must
  // not surface as an unhandled rejection in the meantime.
  const guard = <T>(p: Promise<T>): Promise<T> => {
    p.catch(() => {});
    return p;
  };
  const prefetched: PrefetchedImportResources = {};
  if (importData.snapshotUrl && importData.databaseUrl) {
    if (importData.themeOverrideUrl) {
      prefetched.themeOverride = guard(
        fetch(importData.themeOverrideUrl, { credentials: 'include' }),
      );
    }
    prefetched.snapshot = guard(
      fetchSnapshotWithFallback(
        importData.snapshotUrl,
        importData.snapshotConversionUrl,
        'include',
      ),
    );
    prefetched.database = guard(
      fetch(importData.databaseUrl, { credentials: 'include' }),
    );
  } else if (importData.snapshotUrl) {
    prefetched.snapshot = guard(
      fetchSnapshotWithFallback(
        importData.snapshotUrl,
        importData.snapshotConversionUrl,
        'include',
      ),
    );
    if (importData.themeOverrideUrl) {
      prefetched.themeOverride = guard(
        fetch(importData.themeOverrideUrl, { credentials: 'include' }),
      );
    }
  } else if (importData.themeOverrideUrl && !importData.themeUrl) {
    prefetched.themeOverride = guard(
      fetch(importData.themeOverrideUrl, { credentials: 'include' }),
    );
  } else if (importData.themeUrl) {
    prefetched.theme = guard(
      fetch(importData.themeUrl, { credentials: 'include' }),
    );
    if (importData.databaseUrl) {
      prefetched.database = guard(
        fetch(importData.databaseUrl, { credentials: 'include' }),
      );
    }
  }
  return prefetched;
}

/**
 * Add import-specific blueprint steps.
 * Branches based on the import format:
 * - snapshotUrl + databaseUrl: export-to-cloud path (MySQL source, per-file restore via runSql)
 * - snapshotUrl only: Playground snapshot restore (SQLite, importWordPressFiles)
 * - themeOverrideUrl only: theme-only restore (post-conversion, pre-snapshot)
 * - themeUrl + uploads: per-file restore (import-zip flow)
 */
async function addImportSteps(
  steps: object[],
  siteId: string,
  importData: ImportData,
  prefetched: PrefetchedImportResources,
  onProgress?: (message: string, progress: number) => void,
): Promise<void> {
  if (importData.snapshotUrl && importData.databaseUrl) {
    // Export-to-cloud path: MySQL export from a Docker plugin site.
    // The ZIP has database.sql (MySQL) but NO SQLite database.
    //
    // DO NOT use importWordPressFiles here. That step runs defineSiteUrl,
    // upgrade.php, and replaceSiteUrl on the database, which conflicts with
    // the MySQL-to-SQLite import via runSql. Instead, use the same proven
    // per-file approach: installTheme → extract uploads → runSql → fixup PHP.
    //
    // Uploads and plugins are extracted from the snapshot ZIP using PHP's
    // ZipArchive directly, avoiding importWordPressFiles side effects.

    // 1. Install theme from the dedicated converted theme ZIP
    if (importData.themeOverrideUrl) {
      onProgress?.('Installing your theme...', 20);
      const themeRes = await (prefetched.themeOverride ??
        fetch(importData.themeOverrideUrl, {
          credentials: 'include',
        }));
      if (!themeRes.ok) {
        throw new Error(`Failed to fetch theme override: ${themeRes.status}`);
      }
      const themeZipBytes = new Uint8Array(await themeRes.arrayBuffer());
      steps.push({
        step: 'installTheme',
        themeData: {
          resource: 'literal',
          name: 'theme.zip',
          contents: themeZipBytes,
        },
        options: { activate: true },
      });
    }

    // 2. Extract plugin and upload files from the snapshot ZIP via PHP.
    // Theme files are installed separately from the protected theme ZIP.
    onProgress?.('Restoring your workspace files...', 25);
    const snapshotBytes = await (prefetched.snapshot ??
      fetchSnapshotWithFallback(
        importData.snapshotUrl,
        importData.snapshotConversionUrl,
        'include',
      ));

    steps.push({
      step: 'writeFile',
      path: '/tmp/snapshot.zip',
      data: {
        resource: 'literal',
        name: 'snapshot.zip',
        contents: snapshotBytes,
      },
    });

    steps.push({
      step: 'runPHP',
      code: SNAPSHOT_PLUGIN_UPLOAD_EXTRACTOR_PHP,
    });

    // 3. Import database and fix URLs
    onProgress?.('Importing your content...', 35);
    await addDatabaseImportSteps(
      steps,
      siteId,
      importData.databaseUrl,
      prefetched.database,
    );
  } else if (importData.snapshotUrl) {
    // Playground-to-Playground snapshot restore (has SQLite, no MySQL).
    // importWordPressFiles handles everything including the database.
    onProgress?.('Restoring your workspace...', 20);
    const snapshotBytes = await (prefetched.snapshot ??
      fetchSnapshotWithFallback(
        importData.snapshotUrl,
        importData.snapshotConversionUrl,
        'include',
      ));

    steps.push({
      step: 'importWordPressFiles',
      wordPressFilesZip: {
        resource: 'literal',
        name: 'snapshot.zip',
        contents: snapshotBytes,
      },
    });

    // Never retain connection identity or theme ownership supplied by a ZIP.
    steps.push(getImportedMilesOptionsCleanupStep());

    if (importData.themeOverrideUrl) {
      onProgress?.('Activating your theme...', 30);
      const themeRes = await (prefetched.themeOverride ??
        fetch(importData.themeOverrideUrl, {
          credentials: 'include',
        }));
      if (!themeRes.ok) {
        throw new Error(`Failed to fetch theme override: ${themeRes.status}`);
      }
      const themeZipBytes = new Uint8Array(await themeRes.arrayBuffer());
      steps.push({
        step: 'installTheme',
        themeData: {
          resource: 'literal',
          name: 'theme.zip',
          contents: themeZipBytes,
        },
        options: { activate: true },
      });
    }
  } else if (importData.themeOverrideUrl && !importData.themeUrl) {
    // Theme-only restore: no snapshot yet, but converted theme exists
    // Install just the converted theme on a fresh Playground
    onProgress?.('Installing your theme...', 20);
    const themeRes = await (prefetched.themeOverride ??
      fetch(importData.themeOverrideUrl, {
        credentials: 'include',
      }));
    if (!themeRes.ok) {
      throw new Error(`Failed to fetch converted theme: ${themeRes.status}`);
    }
    const themeZipBytes = new Uint8Array(await themeRes.arrayBuffer());

    steps.push({
      step: 'installTheme',
      themeData: {
        resource: 'literal',
        name: 'theme.zip',
        contents: themeZipBytes,
      },
      options: { activate: true },
    });
  } else if (importData.themeUrl) {
    // Existing path: per-file restore (import-zip flow)
    await addPerFileImportSteps(
      steps,
      siteId,
      importData,
      prefetched,
      onProgress,
    );
  } else {
    console.warn(
      '[Blueprint] Import data present but no actionable URLs — no import steps added',
      {
        snapshotUrl: importData.snapshotUrl,
        themeOverrideUrl: importData.themeOverrideUrl,
        themeUrl: importData.themeUrl,
      },
    );
  }
}

/**
 * Per-file import steps: theme, uploads, database, URL rewriting.
 * Used by the import-zip flow (unchanged from original).
 */
async function addPerFileImportSteps(
  steps: object[],
  siteId: string,
  importData: ImportData,
  prefetched: PrefetchedImportResources,
  onProgress?: (message: string, progress: number) => void,
): Promise<void> {
  // 1. Install theme
  onProgress?.('Installing your theme...', 20);
  const themeRes = await (prefetched.theme ??
    fetch(importData.themeUrl!, {
      credentials: 'include',
    }));
  if (!themeRes.ok) {
    throw new Error(`Failed to fetch imported theme: ${themeRes.status}`);
  }
  const themeZipBytes = new Uint8Array(await themeRes.arrayBuffer());

  steps.push({
    step: 'installTheme',
    themeData: {
      resource: 'literal',
      name: 'theme.zip',
      contents: themeZipBytes,
    },
    options: { activate: true },
  });

  const uploads = importData.uploads || [];

  // 2. Create upload directories
  const uploadDirs = new Set<string>();
  for (const upload of uploads) {
    const fullPath = `/wordpress/wp-content/uploads/${upload.path}`;
    const dir = fullPath.split('/').slice(0, -1).join('/');
    uploadDirs.add(dir);
  }
  if (uploadDirs.size > 0) {
    const mkdirCode = Array.from(uploadDirs)
      .map((dir) => `@mkdir('${dir}', 0777, true);`)
      .join('\n');
    steps.push({
      step: 'runPHP',
      code: `<?php\n${mkdirCode}\n`,
    });
  }

  // 3. Write upload files to Playground filesystem. Fetched concurrently
  // (the browser's per-origin connection cap bounds parallelism); steps are
  // pushed in the original uploads order, and a failed fetch keeps the
  // existing warn-and-skip behavior because the catch lives inside the
  // per-upload function.
  if (uploads.length > 0) {
    onProgress?.('Loading your images...', 28);
  }
  const uploadSteps = await Promise.all(
    uploads.map(async (upload): Promise<object | null> => {
      try {
        const uploadRes = await fetch(upload.url, { credentials: 'include' });
        if (!uploadRes.ok) {
          console.warn(
            `[Blueprint] Failed to fetch upload ${upload.path}: ${uploadRes.status}`,
          );
          return null;
        }
        const uploadBytes = new Uint8Array(await uploadRes.arrayBuffer());
        return {
          step: 'writeFile',
          path: `/wordpress/wp-content/uploads/${upload.path}`,
          data: {
            resource: 'literal',
            name: upload.path.split('/').pop() || 'file',
            contents: uploadBytes,
          },
        };
      } catch (e) {
        console.warn(`[Blueprint] Failed to fetch upload ${upload.path}:`, e);
        return null;
      }
    }),
  );
  for (const uploadStep of uploadSteps) {
    if (uploadStep) steps.push(uploadStep);
  }

  // 4. Import database and fix URLs
  if (importData.databaseUrl) {
    onProgress?.('Importing your content...', 35);
    await addDatabaseImportSteps(
      steps,
      siteId,
      importData.databaseUrl,
      prefetched.database,
    );
  }
}

/**
 * Add database import steps: run SQL, reset admin, rewrite URLs for Playground scope.
 *
 * The exported SQL has root-relative URLs (/wp-content/uploads/...) for portability.
 * In Playground's scoped environment, the service worker only serves files under
 * /scope:site-{id}/, so we rewrite URLs to use the full scope prefix.
 *
 * Uses serialized-safe replacement for wp_postmeta and wp_options (which often
 * contain serialized PHP data where a raw SQL REPLACE would corrupt string lengths).
 */
async function addDatabaseImportSteps(
  steps: object[],
  siteId: string,
  databaseUrl: string,
  databasePromise?: Promise<Response>,
): Promise<void> {
  const dbRes = await (databasePromise ??
    fetch(databaseUrl, { credentials: 'include' }));
  if (!dbRes.ok) {
    throw new Error(`Failed to fetch database.sql: ${dbRes.status}`);
  }

  // Read SQL as text so we can remap the table prefix to wp_
  // (WordPress hosts like SpinupWP use custom prefixes like nvp_, wpa_, etc.)
  let dbSql = await dbRes.text();
  dbSql = remapTablePrefix(dbSql);
  const dbSqlBytes = new TextEncoder().encode(dbSql);

  steps.push({
    step: 'runSql',
    sql: {
      resource: 'literal',
      name: 'database.sql',
      contents: dbSqlBytes,
    },
  });

  const scopeUrl = `${PLAYGROUND_ORIGIN}/scope:site-${siteId}`;

  steps.push({
    step: 'runPHP',
    code: buildPostImportPHP(scopeUrl),
  });
}

/**
 * Build the PHP code that runs after database import to fix admin user and URLs.
 */
function buildPostImportPHP(scopeUrl: string): string {
  return `<?php
require '/wordpress/wp-load.php';
global $wpdb;

// Ensure an 'admin' user exists with the correct password for Playground's
// auto-login mu-plugin.  The runSql step may have imported the original site's
// wp_users table, or it may have failed for that table (MySQL-to-SQLite
// translation issues).  Handle both cases:
//   1. User exists → reset password + ensure administrator role
//   2. User missing → create from scratch
$admin = get_user_by('login', 'admin');
if ($admin) {
  wp_set_password('password', $admin->ID);
  wp_update_user(array('ID' => $admin->ID, 'user_email' => 'admin@localhost.com'));
  $admin->set_role('administrator');
  delete_user_meta($admin->ID, 'session_tokens');
} else {
  // No admin user after DB import — create one
  $admin_id = wp_insert_user(array(
    'user_login' => 'admin',
    'user_pass'  => 'password',
    'user_email' => 'admin@localhost.com',
    'role'       => 'administrator',
  ));
  if (!is_wp_error($admin_id)) {
    delete_user_meta($admin_id, 'session_tokens');
  }
}

// Fix siteurl and home to match Playground's scope URL
$scope_url = '${scopeUrl}';
update_option('siteurl', $scope_url);
update_option('home', $scope_url);

// Remove Miles-specific options from the imported database.
// The setSiteOptions blueprint step will set fresh values for this Playground instance.
$wpdb->query("DELETE FROM {$wpdb->options} WHERE option_name LIKE 'miles_%'");

// Reactivate Miles plugin — the database import overwrites active_plugins
// with the original site's plugin list, which doesn't include Miles.
require_once ABSPATH . 'wp-admin/includes/plugin.php';
if (!is_plugin_active('miles/miles.php')) {
  $result = activate_plugin('miles/miles.php');
  if (is_wp_error($result)) {
    error_log('Miles plugin activation failed: ' . $result->get_error_message());
  }
}

// --- Serialized-safe search-replace for upload URLs ---
// wp_postmeta and wp_options often contain serialized PHP data where a raw SQL
// REPLACE corrupts string length prefixes. This recurses into unserialized
// structures and re-serializes with correct lengths.

$search = '/wp-content/uploads/';
$replace = $scope_url . '/wp-content/uploads/';

function miles_sr_deep($data, $search, $replace) {
  if (is_string($data)) {
    return str_replace($search, $replace, $data);
  }
  if (is_array($data)) {
    return array_map(function($v) use ($search, $replace) {
      return miles_sr_deep($v, $search, $replace);
    }, $data);
  }
  if (is_object($data)) {
    foreach (get_object_vars($data) as $k => $v) {
      $data->$k = miles_sr_deep($v, $search, $replace);
    }
    return $data;
  }
  return $data;
}

function miles_sr_value($value, $search, $replace) {
  if (is_serialized($value)) {
    $unserialized = @unserialize($value);
    if ($unserialized !== false || $value === 'b:0;') {
      $replaced = miles_sr_deep($unserialized, $search, $replace);
      return serialize($replaced);
    }
  }
  return str_replace($search, $replace, $value);
}

// 1. wp_posts.post_content — block HTML, patterns, templates, global styles
$wpdb->query(
  "UPDATE {$wpdb->posts} SET post_content = REPLACE(post_content, '{$search}', '{$replace}') WHERE post_content LIKE '%/wp-content/uploads/%'"
);

// 2. wp_postmeta — custom fields, plugin data (serialized-safe)
$meta_rows = $wpdb->get_results(
  "SELECT meta_id, meta_value FROM {$wpdb->postmeta} WHERE meta_value LIKE '%/wp-content/uploads/%'"
);
foreach ($meta_rows as $row) {
  $new_value = miles_sr_value($row->meta_value, $search, $replace);
  if ($new_value !== $row->meta_value) {
    $wpdb->update($wpdb->postmeta, array('meta_value' => $new_value), array('meta_id' => $row->meta_id));
  }
}

// 3. wp_options — widgets, custom CSS, plugin settings (serialized-safe)
$opt_rows = $wpdb->get_results(
  "SELECT option_id, option_value FROM {$wpdb->options} WHERE option_value LIKE '%/wp-content/uploads/%'"
);
foreach ($opt_rows as $row) {
  $new_value = miles_sr_value($row->option_value, $search, $replace);
  if ($new_value !== $row->option_value) {
    $wpdb->update($wpdb->options, array('option_value' => $new_value), array('option_id' => $row->option_id));
  }
}

// Flush rewrite rules — the imported database has rewrite rules from the
// original site's URL structure. Without flushing, REST API endpoints
// (templates, global-styles, etc.) won't resolve in Playground's scoped URLs,
// causing the site editor to show blank.
flush_rewrite_rules(true);

// Mark Miles setup as complete so the "Setup Miles" banner doesn't show
update_option('miles_setup_complete', array('timestamp' => time(), 'method' => 'import'));
`;
}

/**
 * Detect the table prefix in an exported SQL dump and remap all table names to wp_.
 *
 * WordPress hosts use various prefixes (wp_, nvp_, wpa_, dbs_, etc.) but
 * Playground always uses wp_. Without remapping, WordPress can't find its tables.
 */
function remapTablePrefix(sql: string): string {
  // Detect prefix from CREATE TABLE for a known core table (options is always present)
  const match = sql.match(/CREATE TABLE[^`]*`([a-zA-Z0-9_]*?)options`/);
  if (!match) return sql;

  const detectedPrefix = match[1];
  if (detectedPrefix === 'wp_') return sql;

  debugLog(`[Blueprint] Remapping table prefix: ${detectedPrefix} → wp_`);

  // Global replace of the old prefix with wp_ throughout the SQL.
  // This covers DROP TABLE, CREATE TABLE, INSERT INTO, ALTER TABLE,
  // foreign key references, and any other occurrences.
  return sql.split(detectedPrefix).join('wp_');
}
